<template>
  <div>
    <p v-if="def.sorted && entries.length > 1" class="text-xs text-gray-500 dark:text-gray-400 mb-2.5">Kept in date order, the latest first. The order follows the dates you enter.</p>
    <ul v-if="entries.length" class="space-y-2.5 mb-3">
      <li v-for="(entry, index) in entries" :key="entry.id" class="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-[#161b22]">
        <div class="flex items-center gap-1 pr-1.5">
          <button type="button" @click="toggle(entry.id)" :aria-expanded="isOpen(entry.id)"
            class="flex-1 min-w-0 flex items-center gap-2 px-3 h-10 text-left rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50">
            <svg class="w-3.5 h-3.5 flex-shrink-0 text-gray-400 transition-transform" :class="isOpen(entry.id) ? 'rotate-90' : ''" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5"/></svg>
            <span class="min-w-0 truncate text-[13px]">
              <span class="font-semibold text-gray-900 dark:text-white">{{ entry[def.title] || `New ${def.noun}` }}</span>
              <span v-if="def.subtitle && entry[def.subtitle]" class="text-gray-500 dark:text-gray-400"> · {{ entry[def.subtitle] }}</span>
            </span>
          </button>
          <button v-if="!def.sorted" type="button" :disabled="index === 0" @click="move(index, -1)" :aria-label="`Move ${def.noun} up`" :class="ICON_BUTTON">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="m4.5 15.75 7.5-7.5 7.5 7.5"/></svg>
          </button>
          <button v-if="!def.sorted" type="button" :disabled="index === entries.length - 1" @click="move(index, 1)" :aria-label="`Move ${def.noun} down`" :class="ICON_BUTTON">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5"/></svg>
          </button>
          <button type="button" @click="remove(index)" :aria-label="`Delete ${def.noun}`" :class="ICON_BUTTON + ' hover:!text-red-600 hover:!bg-red-50 dark:hover:!bg-red-900/20'">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"/></svg>
          </button>
        </div>

        <div v-show="isOpen(entry.id)" class="grid grid-cols-1 sm:grid-cols-2 gap-3 px-3 pb-3 pt-1">
          <template v-for="(field, i) in def.fields" :key="i">
            <template v-if="field.type === 'dates'">
              <FormField v-model="entry.start_date" label="Start" placeholder="Jan 2022" :maxlength="20" />
              <div>
                <FormField v-model="entry.end_date" label="End" :placeholder="entry.current ? 'Present' : 'Dec 2024'" :maxlength="20" />
                <label v-if="field.current" class="mt-1.5 inline-flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300 cursor-pointer">
                  <input v-model="entry.current" type="checkbox" class="rounded border-gray-300 text-blue-600 focus:ring-blue-500">
                  I work here now
                </label>
              </div>
            </template>
            <div v-else-if="field.type === 'bullets'" class="sm:col-span-2">
              <BulletsEditor v-model="entry[field.key]" :label="field.label" :placeholder="field.placeholder" />
            </div>
            <div v-else-if="field.type === 'tags'" class="sm:col-span-2">
              <TagsInput v-model="entry[field.key]" :label="field.label" :placeholder="field.placeholder" />
            </div>
            <div v-else :class="field.wide ? 'sm:col-span-2' : ''">
              <FormField v-model="entry[field.key]" :label="field.label" :type="field.type === 'textarea' || field.type === 'select' ? field.type : 'text'" :options="field.options"
                :rows="field.rows" :placeholder="field.placeholder" :maxlength="field.type === 'textarea' ? 4000 : 200" />
            </div>
          </template>
        </div>
      </li>
    </ul>

    <button type="button" @click="add" :disabled="entries.length >= MAX_ENTRIES"
      class="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-dashed border-gray-300 dark:border-gray-600 text-[13px] font-semibold text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 disabled:opacity-50 disabled:cursor-not-allowed">
      <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/></svg>
      Add {{ def.noun }}
    </button>
  </div>
</template>

<script setup>
import { onBeforeUnmount, onMounted, reactive, watch } from 'vue'
import FormField from './FormField.vue'
import BulletsEditor from './BulletsEditor.vue'
import TagsInput from './TagsInput.vue'
import { blankEntry } from './sectionDefs.js'
import { needsSorting, sortByDate } from './resumeDates.js'

const MAX_ENTRIES = 30 // ResumeContent::MAX_ENTRIES
const ICON_BUTTON = 'w-7 h-7 flex-shrink-0 inline-flex items-center justify-center rounded-md text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30 disabled:pointer-events-none focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50'

// `entries` is the section's array in the resume itself: it is edited in place.
const props = defineProps({
  def: { type: Object, required: true }, // from sectionDefs.js
  entries: { type: Array, required: true },
})

// Which entries are open. A new entry opens, and the rest stay as they were.
const opened = reactive(new Set())
const isOpen = (id) => opened.has(id)
const toggle = (id) => (opened.has(id) ? opened.delete(id) : opened.add(id))

function add() {
  const entry = blankEntry(props.def)
  props.entries.push(entry)
  opened.add(entry.id)
}

function remove(index) {
  props.entries.splice(index, 1)
}

// Experience and education stay in date order. They are put in order when the section opens, and a moment after the dates are changed, so an
// entry does not jump about while its date is still being typed. A new entry (no date yet) stays at the end until it has one.
let sortTimer = null
const datesOf = () => props.entries.map((entry) => `${entry.id}|${entry.start_date}|${entry.end_date}|${entry.current}`).join(';')
function sortNow() {
  if (!props.def.sorted || !needsSorting(props.entries)) return
  props.entries.splice(0, props.entries.length, ...sortByDate(props.entries))
}
onMounted(sortNow)
watch(datesOf, () => {
  clearTimeout(sortTimer)
  sortTimer = setTimeout(sortNow, 900)
})
onBeforeUnmount(() => {
  clearTimeout(sortTimer)
  sortNow()
})

function move(index, step) {
  const [entry] = props.entries.splice(index, 1)
  props.entries.splice(index + step, 0, entry)
}
</script>
